import { useState } from "react";
import { toast } from "sonner";
import { cld, errMsg } from "@/lib/api";
import { uploadFile } from "@/components/admin/UrlUpload";

export const GROUP_DEFAULTS = { Original: "Original Painting", "Canvas Print": "Stretched Canvas", "Fine Art Poster": "Fine Art Paper" };
const SUGGESTED = ["Original Painting", "Stretched Canvas", "Fine Art Paper", "Framed Prints"];

const ImageRow = ({ img, gi, ii, upd, remove }) => (
  <div className="grid grid-cols-12 items-center gap-2" data-testid={`image-group-${gi}-image-${ii}`}>
    <div className="col-span-1 aspect-[4/5] bg-[#F2EEE6]">{img.url && <img src={cld(img.url, "w_80,q_auto,f_auto")} alt="" className="h-full w-full object-cover" />}</div>
    <input className="admin-input col-span-5" placeholder="https://res.cloudinary.com/…" value={img.url} onChange={(e) => upd({ url: e.target.value })} data-testid={`image-group-${gi}-url-${ii}`} />
    <input className="admin-input col-span-3" placeholder="Alt text" value={img.alt || ""} onChange={(e) => upd({ alt: e.target.value })} data-testid={`image-group-${gi}-alt-${ii}`} />
    <label className="col-span-2 flex items-center gap-1 text-xs"><input type="radio" name={`default-${gi}`} checked={!!img.is_default} onChange={() => upd({ is_default: true }, true)} data-testid={`image-group-${gi}-default-${ii}`} /> Default</label>
    <button type="button" className="col-span-1 text-xs text-[#8B2E24]" onClick={remove} data-testid={`image-group-${gi}-remove-${ii}`}>✕</button>
  </div>
);

const GroupBox = ({ g, gi, upd, remove }) => {
  const [busy, setBusy] = useState(false);
  const setImages = (images) => upd({ images });
  const updImg = (ii, patch, exclusive) => setImages(g.images.map((x, n) => (n === ii ? { ...x, ...patch } : exclusive ? { ...x, is_default: false } : x)));
  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      try {
        added.push({ url: await uploadFile(f, "art-store/products"), alt: "", is_default: false });
      } catch (err) {
        toast.error(errMsg(err, `Upload failed: ${f.name}`));
      }
    }
    const next = [...g.images, ...added];
    if (next.length && !next.some((i) => i.is_default)) next[0] = { ...next[0], is_default: true };
    setImages(next);
    setBusy(false);
    if (added.length) toast.success(`${added.length} image${added.length > 1 ? "s" : ""} uploaded`);
  };
  return (
    <div className="space-y-2 border border-[#E5E0D8] p-4" data-testid={`image-group-${gi}`}>
      <div className="flex items-center gap-3">
        <input list="image-group-names" className="admin-input" placeholder="Group name, e.g. Fine Art Paper" value={g.group_name} onChange={(e) => upd({ group_name: e.target.value })} data-testid={`image-group-${gi}-name`} />
        <button type="button" className="whitespace-nowrap text-xs text-[#8B2E24] underline underline-offset-4" onClick={remove} data-testid={`image-group-${gi}-delete`}>Remove group</button>
      </div>
      {g.images.map((img, ii) => (
        <ImageRow key={ii} img={img} gi={gi} ii={ii} upd={(p, ex) => updImg(ii, p, ex)} remove={() => setImages(g.images.filter((_, n) => n !== ii))} />
      ))}
      <div className="flex gap-5 pt-1 text-xs">
        <label className="cursor-pointer underline underline-offset-4">
          {busy ? "Uploading…" : "Upload images"}
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={busy} data-testid={`image-group-${gi}-files`} />
        </label>
        <button type="button" className="underline underline-offset-4" onClick={() => setImages([...g.images, { url: "", alt: "", is_default: !g.images.length }])} data-testid={`image-group-${gi}-add-url`}>+ Add by URL</button>
      </div>
    </div>
  );
};

export const ImageGroupsEditor = ({ groups, set }) => (
  <div className="col-span-2 space-y-3" data-testid="image-groups-editor">
    <span className="field-label">Image groups — each group becomes a tab on the artwork page</span>
    <datalist id="image-group-names">{SUGGESTED.map((s) => <option key={s} value={s} />)}</datalist>
    {groups.map((g, gi) => (
      <GroupBox key={gi} g={g} gi={gi} upd={(patch) => set(groups.map((x, n) => (n === gi ? { ...x, ...patch } : x)))} remove={() => set(groups.filter((_, n) => n !== gi))} />
    ))}
    <button type="button" className="text-xs underline underline-offset-4" onClick={() => set([...groups, { group_name: "", images: [] }])} data-testid="image-group-add-btn">+ Add image group</button>
  </div>
);
